import { useState, type ReactNode } from "react";
import { Erase, DragHandGesture, Flash, Leaf, Undo, Redo, Xmark } from "iconoir-react";
import type { BoardMode, Tool } from "../game/useGame";
import type { AssistSettings, PlayMode } from "../lib/storage";

type Props = {
  tool: Tool;
  playMode: PlayMode;
  boardMode: BoardMode;
  canUndo: boolean;
  canRedo: boolean;
  completed: boolean;
  zoom: number;
  assists: AssistSettings;
  onToolChange: (tool: Tool) => void;
  onPlayModeChange: (mode: PlayMode) => void;
  onBoardModeChange: (mode: BoardMode) => void;
  onUndo: () => void;
  onRedo: () => void;
  onCheck: () => void;
  onZoomChange: (zoom: number) => void;
  onAssistsChange: (assists: AssistSettings) => void;
};

/** A filled square is the mark itself, not a picture of it, so it is drawn to the
    same 24-unit box as the iconoir glyphs beside it rather than set as text. */
function FillMark() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" fill="currentColor" />
    </svg>
  );
}

// Draw and Move answer the same question — what a touch on the board does — so they
// are one four-way choice, not a tool group plus a separate mode toggle that had to
// silently move itself whenever a tool was picked.
const BOARD_TOOLS: Array<{ id: Tool | "move"; icon: ReactNode; label: string; shortcut: string }> = [
  { id: "fill", icon: <FillMark />, label: "Fill", shortcut: "F" },
  { id: "cross", icon: <Xmark width={18} height={18} strokeWidth={2} />, label: "Cross", shortcut: "X" },
  { id: "erase", icon: <Erase width={18} height={18} strokeWidth={2} />, label: "Erase", shortcut: "⌫" },
  { id: "move", icon: <DragHandGesture width={18} height={18} strokeWidth={2} />, label: "Move", shortcut: "M" },
];

const ZOOM_MIN = 0.7;
const ZOOM_MAX = 1.6;
const ZOOM_STEP = 0.1;

function formatZoom(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}

export function GameControls({
  tool,
  playMode,
  boardMode,
  canUndo,
  canRedo,
  completed,
  zoom,
  assists,
  onToolChange,
  onPlayModeChange,
  onBoardModeChange,
  onUndo,
  onRedo,
  onCheck,
  onZoomChange,
  onAssistsChange,
}: Props) {
  const [assistsOpen, setAssistsOpen] = useState(false);
  const selected = boardMode === "move" ? "move" : tool;

  function nudgeZoom(delta: number): void {
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((zoom + delta) * 10) / 10));
    onZoomChange(next);
  }

  return (
    <div className="game-controls" aria-label="Puzzle tools">
      {/* Row one is the quiet row: how the puzzle judges you and how much of the
          board you can see. Neither is touched more than once or twice a game, so
          it sits furthest from the grid. */}
      <div className="controls-modes">
        {/* Challenge calls out a wrong square the moment it is marked — hence the
            spark. Relaxed says nothing until the player asks, which is the only way
            to sit with a wrong guess long enough to reason your way out of it. */}
        <div className="play-mode-group" role="group" aria-label="Play mode">
          <button
            className={playMode === "challenge" ? "mode-button is-active" : "mode-button"}
            type="button"
            aria-pressed={playMode === "challenge"}
            disabled={completed}
            onClick={() => onPlayModeChange("challenge")}
          >
            <Flash width={14} height={14} strokeWidth={2} aria-hidden="true" />
            <span className="mode-label-full">Challenge</span>
            <span className="mode-label-short">Chall</span>
          </button>
          <button
            className={playMode === "relaxed" ? "mode-button is-active" : "mode-button"}
            type="button"
            aria-pressed={playMode === "relaxed"}
            disabled={completed}
            onClick={() => onPlayModeChange("relaxed")}
          >
            <Leaf width={14} height={14} strokeWidth={2} aria-hidden="true" />
            <span className="mode-label-full">Relaxed</span>
            <span className="mode-label-short">Relax</span>
          </button>
        </div>

        <div className="zoom-controls" role="group" aria-label="Board zoom">
          <button className="text-button" type="button" aria-label="Zoom out" disabled={zoom <= ZOOM_MIN} onClick={() => nudgeZoom(-ZOOM_STEP)}>−</button>
          <button className="text-button zoom-reset" type="button" aria-label="Reset zoom" onClick={() => onZoomChange(1)}>
            {formatZoom(zoom)}
          </button>
          <button className="text-button" type="button" aria-label="Zoom in" disabled={zoom >= ZOOM_MAX} onClick={() => nudgeZoom(ZOOM_STEP)}>+</button>
        </div>
      </div>

      {/* Row two is the working row, kept next to the grid: what a touch puts on the
          board, then what to do about the last one. */}
      <div className="controls-workspace">
        <div className="tool-group" role="group" aria-label="Board tool">
          {BOARD_TOOLS.map((item) => (
            <button
              className={selected === item.id ? "tool-button is-active" : "tool-button"}
              key={item.id}
              type="button"
              aria-label={item.label}
              aria-pressed={selected === item.id}
              disabled={completed}
              onClick={() => (item.id === "move" ? onBoardModeChange("move") : onToolChange(item.id))}
            >
              <span className="tool-symbol" aria-hidden="true">{item.icon}</span>
              <span className="control-label">{item.label}</span>
              <kbd>{item.shortcut}</kbd>
            </button>
          ))}
        </div>

        <div className="history-actions">
          {/* Challenge flags a wrong square as it is made, so Check has nothing to
              report there — but it keeps its slot so Undo and Redo do not slide
              sideways the moment the play mode changes. */}
          <button
            className={playMode === "relaxed" ? "text-button" : "text-button is-hidden"}
            type="button"
            aria-label="Check board"
            aria-hidden={playMode === "relaxed" ? undefined : true}
            tabIndex={playMode === "relaxed" ? undefined : -1}
            disabled={completed || playMode !== "relaxed"}
            onClick={onCheck}
          >
            <span className="control-label">Check</span>
          </button>
          <button className="text-button" type="button" aria-label="Undo" disabled={!canUndo || completed} onClick={onUndo}>
            <Undo width={18} height={18} strokeWidth={2} aria-hidden="true" /> <span className="control-label">Undo</span>
          </button>
          <button className="text-button" type="button" aria-label="Redo" disabled={!canRedo || completed} onClick={onRedo}>
            <Redo width={18} height={18} strokeWidth={2} aria-hidden="true" /> <span className="control-label">Redo</span>
          </button>
        </div>
      </div>

      {/* Row three is opt-in help, folded away so the default board stays exactly
          the puzzle. Both switches are things a careful player does by hand; turning
          them on only saves the transcription. */}
      <div className="assist-controls">
        <button
          className="text-button assists-toggle"
          type="button"
          aria-expanded={assistsOpen}
          aria-controls="assist-panel"
          onClick={() => setAssistsOpen((open) => !open)}
        >
          <span className="control-label">Assists</span>
          <span aria-hidden="true">{assistsOpen ? "−" : "+"}</span>
        </button>
        {assistsOpen && (
          <div className="assist-panel" id="assist-panel">
            <label className="assist-option">
              <input
                type="checkbox"
                checked={assists.autoCrossLines}
                onChange={(event) => onAssistsChange({ ...assists, autoCrossLines: event.target.checked })}
              />
              <span>
                <b>Auto-cross finished lines</b>
                <small>When a line's clues are all met, mark its remaining squares as empty.</small>
              </span>
            </label>
            <label className="assist-option">
              <input
                type="checkbox"
                checked={assists.tapCycles}
                onChange={(event) => onAssistsChange({ ...assists, tapCycles: event.target.checked })}
              />
              <span>
                <b>Cycle taps</b>
                <small>One tap walks a square through fill, cross, and empty — no tool switching.</small>
              </span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
